/**
 * Property-level assumption sets on one deal, and their roll-up.
 *
 * A construction loan across several properties is one deal with N
 * underwritten properties. Each set is one property's figures; the deal's
 * figures are their roll-up — sums where the field is additive, ratios
 * re-derived from the summed legs, never averaged.
 */
import api from '@/services/api'

export type AssumptionValue = {
  field: string
  label: string
  kind: string
  unit: string | null
  value: number | null
  source: string | null
  valueState: string
}

export type AssumptionSet = {
  id: string
  loanId: string
  name: string
  assetType: string | null
  address: string | null
  propertyId: string | null
  workbookVersionId: string | null
  sourceSheet: string | null
  sequence: number
  scenario: string
  loanAllocation: number | null
  note: string | null
  values: AssumptionValue[]
  createdBy: string
  createdAt: string | null
  updatedBy: string | null
  updatedAt: string | null
}

export type RolledField = {
  field: string
  value: number
  method: string
  contributors: { setId: string; name: string; value: number; weight?: number }[]
}

export type AssumptionField = {
  name: string
  label: string
  kind: string
  unit: string | null
  group: string
  rolledUp: boolean
}

export type AssumptionSetsPayload = {
  loanId: string
  scenario: string
  sets: AssumptionSet[]
  rollup: Record<string, RolledField>
  fields: AssumptionField[]
  warnings: string[]
  created?: string[]
  written?: string[]
}

const base = (loanId: string) => `/loans/${loanId}/assumption-sets`

export const assumptionSetsService = {
  list: (loanId: string, scenario = 'base') =>
    api.get(base(loanId), { params: { scenario } }).then((r) => r.data as AssumptionSetsPayload),

  create: (loanId: string, body: { name: string; loanAllocation?: number | null; assetType?: string | null; address?: string | null }) =>
    api.post(base(loanId), body).then((r) => r.data as AssumptionSetsPayload),

  update: (loanId: string, setId: string, body: { name?: string; loanAllocation?: number | null; clearLoanAllocation?: boolean; address?: string | null; note?: string | null }) =>
    api.put(`${base(loanId)}/${setId}`, body).then((r) => r.data as AssumptionSetsPayload),

  withdraw: (loanId: string, setId: string) =>
    api.delete(`${base(loanId)}/${setId}`).then((r) => r.data as AssumptionSetsPayload),

  putValues: (loanId: string, setId: string, values: { field: string; value: number | null }[]) =>
    api.put(`${base(loanId)}/${setId}/values`, { values }).then((r) => r.data as AssumptionSetsPayload),

  fromWorkbook: (loanId: string, versionId: string) =>
    api.post(`${base(loanId)}/from-workbook`, { versionId }).then((r) => r.data as AssumptionSetsPayload),

  apply: (loanId: string) =>
    api.post(`${base(loanId)}/apply`, {}).then((r) => r.data as AssumptionSetsPayload),
}
